import { flushAll, hasPendingRemark, hasPendingWork } from '../features/editor/saveQueue.ts'
import { flushResponses } from '../features/learner/responseQueue.ts'
import { reportProblem } from '../lib/telemetry.ts'
import {
	entryOf,
	isChunkLoadError,
	isNewBuild,
	type Recovery,
	recoverFromStaleBuild,
	retryRouteLoad,
} from './chunkRecovery.ts'

/**
 * The browser side of a deploy that happened under an open tab: this composes the pure rules in
 * chunkRecovery.ts with the editor's save queue, which is the only place unsaved work lives.
 */

/**
 * Saves the editor's pending edits and says whether any are left. A student's answers still on their way
 * are sent too, best effort: they are kept on the device either way, so they survive the reload.
 */
export async function editsSaved(): Promise<boolean> {
	await Promise.all([flushAll(), flushResponses().catch(() => {})])
	return !hasPendingWork() && !hasPendingRemark()
}

/**
 * Whether the server now serves a different build than this tab runs: the shell is fetched past every
 * cache and its entry script compared with the one this page loaded. Offline, a failed or slow fetch, or
 * a page that names no entry all answer no: none of them is evidence of a deploy.
 */
export async function newBuildOnServer(): Promise<boolean> {
	if (navigator.onLine === false) return false
	const running = document.querySelector('script[type="module"][src]')?.getAttribute('src') ?? null
	const ask = new AbortController()
	const timer = setTimeout(() => ask.abort(), 5000)
	try {
		const res = await fetch('/app', { cache: 'no-store', credentials: 'omit', signal: ask.signal })
		return res.ok && isNewBuild(running, entryOf(await res.text()))
	} catch {
		return false
	} finally {
		clearTimeout(timer)
	}
}

let inFlight: Promise<Recovery> | null = null

/**
 * One recovery at a time: the router's error screen and the page-wide listeners both see the same
 * failure and must not race each other into a "tried already" message while the reload is coming.
 */
export function recover(): Promise<Recovery> {
	inFlight ??= recoverFromStaleBuild({
		newBuild: newBuildOnServer,
		storage: sessionStorage,
		saved: editsSaved,
		reload: () => location.reload(),
	}).finally(() => {
		inFlight = null
	})
	return inFlight
}

let retrying: Promise<boolean> | null = null

/**
 * The route's own code failed to load and the server has no newer build: one reload (retryRouteLoad).
 * Shared, so two failing routes at once do not have one reload while the other reports and shows the error.
 */
export function retryRoute(): Promise<boolean> {
	retrying ??= retryRouteLoad({
		online: navigator.onLine !== false,
		storage: sessionStorage,
		saved: editsSaved,
		reload: () => location.reload(),
		pageStart: performance.timeOrigin,
	}).then((reloading) => {
		if (!reloading) retrying = null
		return reloading
	})
	return retrying
}

/**
 * Page-wide: a lazy import that fails anywhere (a sheet, a viewer, not only a route) after a deploy
 * reloads the tab once instead of leaving a control that does nothing. Vite fires `vite:preloadError`
 * for ANY chunk or stylesheet it cannot fetch, a network blip included, and the same failure also
 * surfaces as a rejected import; so a failure only reloads when `newBuildOnServer` confirms a deploy.
 * Otherwise nothing happens here and the caller's own handling runs (the PDF loader retries the next
 * time, the router shows its own screen). The event is never cancelled.
 */
export function installStaleBuildRecovery(): void {
	const go = () =>
		void recover().then((r) => {
			if (r === 'unsaved') reportProblem('stale build: reload withheld, edits are not saved')
		})
	window.addEventListener('vite:preloadError', go)
	window.addEventListener('unhandledrejection', (e) => {
		if (isChunkLoadError(e.reason)) go()
	})
}
